<template>
  <div v-if="showNoPropertyMessage">
    <p class="property_error--heading">There was an issue pulling property data for this address.</p>
    <p class="property_error--message">Error: {{ property.real_estate_property_data_error_message }}</p>
  </div>

  <div v-else class="property_modal">
    <div class="info-container flex p-50 mb-150">
      <icon-default size="is-small is-text mr-25" icon="info"/>
      <p class="is-italic">
        Property data is sourced from official county records and may differ slightly from Zillow or <a href="https://realtor.com/" target="_blank">Realtor.com</a>.
        This is expected. Other platforms pull from MLS or user-submitted updates, which update faster than county records.
        This is not a data error.
      </p>
    </div>

    <div class="flex flex-end">
      <button-outlined @click="openPrintPage">View Printable Page</button-outlined>
    </div>

    <property-modal-section
      heading="Owner Information"
      :upgrade-required="!hasMidTierPropertyDataFeature"
    >
      <property-modal-item label="Primary Owner" :value="ownerInfo.owner1FullName" column-class="is-6"/>
      <property-modal-item label="Secondary Owner" :value="ownerInfo.owner2FullName ? ownerInfo.owner2FullName : '--'"
                           column-class="is-6"/>
      <property-modal-item label="Owner Type" :value="ownerInfo.owner1Type" column-class="is-6"/>
      <property-modal-item label="Ownership Length" :value="ownershipLength" column-class="is-6"/>
      <property-modal-item label="Mailing Address" :value="ownerInfo.mailAddress.label" column-class="is-12"/>
    </property-modal-section>

    {{ /* This section will be editable by the user */ }}
    <property-modal-section
      heading="General Details"
      show-edit-button
      @clicked="openPropertyEditModal"
      :is-edit-button-loading="isLoadingInfo"
    >
      <property-modal-item
        label="Bedrooms"
        :value="formatValue(property.bedrooms)"
      />
      <property-modal-item
        label="Stories"
        :value="formatValue(property.stories)"
      />
      <property-modal-item
        label="Basement Size"
        :value="formatValue(property.basement_size)"
      />
      <property-modal-item
        label="Bathrooms"
        :value="formatValue(property.bathrooms)"
      />
      <property-modal-item
        label="Parking Type"
        :value="formatValue(property.parking_type)"
      />
      <property-modal-item
        label="Construction Type"
        :value="formatValue(property.construction_type)"
      />
      <property-modal-item
        label="Partial Bathrooms"
        :value="formatValue(property.partial_bathrooms)"
      />
      <property-modal-item
        label="Pool"
        :value="formatBoolean(property.pool)"
      />
      <property-modal-item
        label="Architecture Type"
        :value="formatValue(property.architecture_type)"
      />
      <property-modal-item
        label="House Size"
        :value="formatValue(property.house_size)"
      />
      <property-modal-item
        label="Fireplaces"
        :value="formatValue(property.fireplaces)"
      />
      <property-modal-item
        label="Mobile Home"
        :value="formatBoolean(property.mobile_home)"
      />
      <property-modal-item
        label="Lot Size"
        :value="formatValue(property.lot_size)"
      />
      <property-modal-item
        label="Heating Type"
        :value="formatValue(property.heating_type)"
      />
      <property-modal-item
        label="Flood Zone"
        :value="formatBoolean(property.flood_zone)"
      />
      <property-modal-item
        label="Year Built"
        :value="formatValue(property.year_built)"
      />
      <property-modal-item
        label="Cooling Type"
        :value="formatValue(property.cooling_type)"
      />
      <property-modal-item
        label="Amenities"
        :value="formatValue(property.amenities)"
        column-class="is-12"
      />
    </property-modal-section>

    <property-modal-section
      heading="Assessment and Taxes"
    >
      <property-modal-item label="Assessment Year" :value="taxInfo.assessmentYear"/>
      <property-modal-item label="Tax Year" :value="taxInfo.year"/>
      <property-modal-item label="Tax Amount" :value="formatCurrency(taxInfo.taxAmount)"/>
      <property-modal-item label="Assessed Value" :value="formatCurrency(taxInfo.assessedValue)"/>
      <property-modal-item label="Land Value" :value="formatCurrency(taxInfo.assessedLandValue)"/>
      <property-modal-item label="Improvement Value" :value="formatCurrency(taxInfo.assessedImprovementValue)"/>
      <property-modal-item label="Market Value" :value="formatCurrency(taxInfo.marketValue)"/>
      <property-modal-item label="Land Value" :value="formatCurrency(taxInfo.marketLandValue)"/>
      <property-modal-item label="Improvement Value" :value="formatCurrency(taxInfo.marketImprovementValue)"/>
    </property-modal-section>

    <property-modal-section
      heading="Geographic Information"
      :upgrade-required="!hasMidTierPropertyDataFeature"
    >
      <property-modal-item label="APN" :value="geographicInfo.apn"/>
      <property-modal-item label="Legal Description" :value="geographicInfo.legalDescription" column-class="is-8"/>
      <property-modal-item label="Property Use" :value="geographicInfo.propertyUse"/>
      <property-modal-item label="Land Use" :value="geographicInfo.landUse"/>
      <property-modal-item label="Census Tract" :value="geographicInfo.censusTract"/>
      <property-modal-item label="Neighborhood" :value="geographicInfo.name"/>
      <property-modal-item label="Neighborhood Type" :value="geographicInfo.type"/>
      <property-modal-item label="Lot Number" :value="geographicInfo.lotNumber"/>
    </property-modal-section>

    <property-modal-section
      heading="Current Mortgage"
      :upgrade-required="!hasMidTierPropertyDataFeature"
    >
      <property-modal-item label="Mortgage Balance" :value="formatCurrency(currentMortgage.openMortgageBalance)"/>
      <property-modal-item label="Equity" :value="formatCurrency(currentMortgage.estimatedEquity)"/>
      <property-modal-item label="High Equity" :value="formatBoolean(currentMortgage.highEquity)"/>
      <property-modal-item label="Mortgage Payment" :value="formatCurrency(currentMortgage.estimatedMortgagePayment)"/>
      <property-modal-item label="Equity Percent" :value="formatPercent(currentMortgage.equityPercent)"/>
      <property-modal-item label="Free & Clear" :value="formatBoolean(currentMortgage.freeClear)"/>
    </property-modal-section>

    <property-modal-wrapper
      heading="Transaction History"
      :upgrade-required="!hasAdvancedTierPropertyDataFeature"
    >
      <template v-for="transaction in transactions">
        {{ /* sale */ }}
        <property-modal-section
          v-if="transaction.type === 'sale'"
          :heading="`Sale - ${formatDate(transaction.recordingDate)}`"
        >
          <property-modal-item
            label="Recording Date"
            :value="formatDate(transaction.recordingDate)"
            column-class="is-6"
          />
          <property-modal-item
            label="Sale Date"
            :value="formatDate(transaction.saleDate)"
            column-class="is-6"
          />
          <property-modal-item
            label="Sale Amount"
            :value="formatCurrency(transaction.saleAmount)"
            column-class="is-6"
          />
          <property-modal-item
            label="Down Payment"
            :value="formatCurrency(transaction.downPayment)"
            column-class="is-6"
          />
          <property-modal-item
            label="Sellers"
            :value="formatValue(transaction.sellerNames)"
            column-class="is-6"
          />
          <property-modal-item
            label="Purchase Method"
            :value="formatValue(transaction.purchaseMethod)"
            column-class="is-6"
          />
          <property-modal-item
            label="Buyers"
            :value="formatValue(transaction.buyerNames)"
            column-class="is-6"
          />
          <property-modal-item
            label="Transaction Type"
            :value="formatValue(transaction.transactionType)"
            column-class="is-6"
          />
          <property-modal-item
            label="Loan to Value"
            :value="formatPercent(transaction.ltv)"
            column-class="is-6"
          />
          <property-modal-item
            label="Document Type"
            :value="formatValue(transaction.documentType)"
            column-class="is-6"
          />
        </property-modal-section>

        {{ /* mortgage */ }}
        <property-modal-section
          v-if="transaction.type === 'mortgage'"
          :heading="`Mortgage - ${formatDate(transaction.recordingDate)}`"
        >
          <property-modal-item
            label="Recording Date"
            :value="formatDate(transaction.recordingDate)"

            column-class="is-
            6"
          />
          <property-modal-item
            label="Document Date"
            :value="formatDate(transaction.documentDate)"
            column-class="is-6"
          />
          <property-modal-item
            label="Amount"
            :value="formatCurrency(transaction.amount)"
            column-class="is-6"
          />
          <property-modal-item
            label="Loan Type"
            :value="formatValue(transaction.loanType)"
            column-class="is-6"
          />
          <property-modal-item
            label="Lender Name"
            :value="formatValue(transaction.lenderName)"
            column-class="is-6"
          />
          <property-modal-item
            label="Lender Type"
            :value="formatValue(transaction.lenderType)"
            column-class="is-6"
          />
          <property-modal-item
            label="Interest Rate"
            :value="formatPercent(transaction.interestRate)"
            column-class="is-6"
          />
          <property-modal-item
            label="Interest Rate Type"
            :value="formatValue(transaction.interestRateType)"
            column-class="is-6"
          />
          <property-modal-item
            label="Grantee Name"
            :value="formatValue(transaction.granteeName)"
            column-class="is-6"
          />
          <property-modal-item
            label="Transaction Type"
            :value="formatValue(transaction.transactionType)"
            column-class="is-6"
          />
          <property-modal-item
            label="Maturity Date"
            :value="formatDate(transaction.maturityDate)"
            column-class="is-6"
          />
          <property-modal-item
            label="Deed Type"
            :value="formatValue(transaction.deedType)"
            column-class="is-6"
          />
          <property-modal-item
            label="Term"
            :value="formatValue(transaction.term)"
            column-class="is-6"
          />
        </property-modal-section>
      </template>
    </property-modal-wrapper>

    <property-modal-wrapper
      heading="School Information"
      :upgrade-required="!hasAdvancedTierPropertyDataFeature"
    >
      <property-modal-section
        v-for="(school, index) in schools"
        :key="index"
        :heading="'School - ' + school.name"
      >
        <property-modal-item
          label="Name"
          :value="formatValue(school.name)"
          column-class="is-6"
        />
        <property-modal-item
          label="Address"
          :value="formatValue(`${school.street}, ${school.city}, ${school.state} ${school.zip}`)"
          column-class="is-6"
        />
        <property-modal-item
          label="Grades"
          :value="formatValue(school.grades)"
        />
        <property-modal-item
          label="Type"
          :value="formatValue(school.type)"
        />
        <property-modal-item
          label="Enrollment"
          :value="formatValue(school.enrollment)"
        />
        <property-modal-item
          label="Rating"
          :value="formatValue(school.rating)"
        />
        <property-modal-item
          label="Parent Rating"
          :value="formatValue(school.parentRating)"
        />
      </property-modal-section>
    </property-modal-wrapper>

    <modal-container>
      <modal-crud-form
        ref="propertyEditDetailModal"
        title="Edit General Details"
        form-component="property-details-form"
        :hideDeleteButton="true"
        is-double-column
      ></modal-crud-form>
    </modal-container>
  </div>
</template>

<script>
import PropertyModalItem from "./components/PropertyModalItem.vue";
import PropertyModalSection from "./components/PropertyModalSection.vue";
import PropertyModalWrapper from "./components/PropertyModalWrapper.vue";
import utils from "../../services/utils";
import dates from "../../services/dates";
import api from "../../api";

export default {
  name: "PropertyModal",
  components: {PropertyModalWrapper, PropertyModalSection, PropertyModalItem},
  props: {
    property: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoadingInfo: false,
    }
  },
  computed: {
    showNoPropertyMessage() {
      return !!this.property.real_estate_property_data_errored_at;
    },
    hasMidTierPropertyDataFeature() {
      return this.$store.state.accounts.features.MidTierPropertyData;
    },
    hasAdvancedTierPropertyDataFeature() {
      return this.$store.state.accounts.features.AdvancedTierPropertyData;
    },
    data() {
      if(this.showNoPropertyMessage) return null;
      return this.property.real_estate_property_data;
    },
    ownerInfo() {
      if(this.data) return this.data.ownerInfo;
      return null;
    },
    ownershipLength() {
      if(this.data) {
        const lengthInMonth = this.data.ownerInfo.ownershipLength
        return (lengthInMonth / 12 | 0) + " years / " + lengthInMonth % 12 +" months"
      }

      return null;
    },
    taxInfo() {
      if(this.data) return this.data.taxInfo;
      return null;
    },
    geographicInfo() {
      const data = this.data;
      if(this.data) return {...data.lotInfo, ...data.neighborhood};
      return null;
    },
    currentMortgage() {
      if(this.data) return this.data;
      return null;
    },
    transactions() {
      if(this.data) {
        const transactions = [];

        this.data.saleHistory.forEach((sale) => {
          transactions.push({
            type: 'sale',
            ...sale,
          })
        })

        this.data.mortgageHistory.forEach((mortgage) => {
          transactions.push({
            type: 'mortgage',
            ...mortgage,
          })
        })

        return transactions;
      }
      return null;
    },
    schools() {
      if(this.data) return this.data.schools;
      return null;
    }
  },
  methods: {
    formatValue(value) {
      if(!value) return '--'
      return value;
    },
    formatBoolean(value) {
      if (value === null || value === undefined) return '--'
      return value ? 'Yes' : 'No';
    },
    formatCurrency(value) {
      if(!value) return '--'
      return utils.formatCurrency(value)
    },
    formatDate(value) {
      if(!value) return '--'
      return dates.formatAsDate(value)
    },
    formatPercent(value) {
      if(!value) return '--'
      return value + '%';
    },
    openPropertyEditModal() {
      this.isLoadingInfo = true
      return api.properties.getProprtyDetailsForm(this.property.id)
        .then(res => {
          this.$refs.propertyEditDetailModal.open(res.data)
        })
        .finally(() => { this.isLoadingInfo = false })
    },
    openPrintPage() {
      this.$router.push({name: 'print-property', query: {back_path: this.$route.fullPath}});
    },
  },
}
</script>

<style lang="scss" scoped>
@import "../../styles/variables/variables";

.info-container {
  border: 1px solid $blue;
  border-radius: 2px;

  p {
    line-height: 1.3;
    font-size: 0.9rem;
  }
}

.property_error {

  &--heading {
    margin: 3rem 0 10px;
    font-size: 1.25rem;
    text-align: center;
  }

  &--message {
    margin-bottom: 4rem;
    text-align: center;
  }
}
</style>
